import dayjs from "dayjs";
import {
  IMetaControlTypesMapping,
  type IMeta,
  type IMetaColumn,
  type IMetaData,
} from "../types/scad";
import {
  InputType,
  type IGenericField,
  type IGenericForm,
} from "../types/generic";

export function getPrettyRecordField(
  record: any,
  column: IMetaColumn,
  meta: IMetaData,
) {
  const foreignKey = meta.FKeys.filter((fKey: any) => {
    return (
      fKey.ColumnNamePc.toUpperCase() === column.ColumnNamePc.toUpperCase()
    );
  });
  if (foreignKey && foreignKey.length > 0) {
    // $"{(ColumnName.EndsWith("Id") ? ColumnName[..^2] : ColumnName)}Join
    const joinName = foreignKey[0].ColumnNamePc.endsWith("Id")
      ? foreignKey[0].ColumnNamePc //.slice(0, -2)
      : foreignKey[0].ColumnNamePc;
    const key = `${joinName}_Join_${column.DisplayColumnName}`;
    return key in record ? record[key] : record[column.ColumnNamePc];
  } else {
    if (column.FkTableName) {
      const key = `${column.ColumnNamePc}_Join_${column.DisplayColumnName}`;
      return key in record ? record[key] : record[column.ColumnNamePc];
    }
  }

  switch (column.ControlType) {
    case "date":
      return column.ColumnNamePc in record && record[column.ColumnNamePc]
        ? dayjs(record[column.ColumnNamePc]).format("DD/MM/YYYY")
        : "";
    case "fileuploadsingle":
      const table =
        meta.Table.TableType == "VIEW" && meta.Table.FKTableName
          ? meta.Table.FKTableName
          : meta.Table.TableName;
      return `/scad/files?TableName=${table}&FilePath=${
        record[column.ColumnNamePc]
      }`;
    case "currency":
      return String(Number(record[column.ColumnNamePc]).toFixed(2));
    default:
      return record[column.ColumnNamePc];
  }
}

export function evaluateValueExpression(
  expression: string,
  data: Indexable = {},
): any {
  if (expression.charAt(0) == "^") return;

  switch (expression) {
    case "true":
      return true;
    case "false":
      return false;
    case "null":
      return null;
    case "@Today":
      return Date();
    case "@All":
      return expression;
    default:
      if (
        expression.charAt(0) == "$" &&
        expression.charAt(1) == "{" &&
        expression.charAt(expression.length - 1) == "}"
      ) {
        return;
      } else if (expression.charAt(0) == "$") {
        return Number(expression.substring(1));
      } else if (
        expression.charAt(0) == "'" &&
        expression.charAt(expression.length - 1) == "'"
      ) {
        return expression.substring(1, expression.length - 1);
      } else if (
        expression.charAt(0) == "[" &&
        expression.charAt(expression.length - 1) == "]"
      ) {
        return JSON.parse(expression);
      } else if (
        expression.charAt(0) == "{" &&
        expression.charAt(expression.length - 1) == "}"
      ) {
        return;
      } else {
        return null;
      }
  }
}

export function ConvertMetaResponse(metaData: IMetaData): IMeta {
  const data = metaData;
  const enabledGridColumns = computed(() => {
    if (!metaData || !metaData) {
      return [];
    }
    return metaData.Columns.filter((col) => {
      return col.ShowInGrid;
    }).sort((a, b) => {
      if (a.GridOrder > b.GridOrder) {
        return 1;
      }
      if (a.GridOrder < b.GridOrder) {
        return -1;
      }
      return 0;
    });
  });
  const enabledEditFormColumns = computed<IMetaColumn[]>(() => {
    if (!metaData || !metaData) {
      return [];
    }
    return metaData.Columns.filter((col) => {
      return col.ShowInEditForm;
    }).sort((a, b) => {
      if (a.EditFormOrder > b.EditFormOrder) {
        return 1;
      }
      if (a.EditFormOrder < b.EditFormOrder) {
        return -1;
      }
      return 0;
    });
  });
  const fieldGroups = computed(() => {
    const groups: string[] = [];
    if (metaData && metaData) {
      enabledEditFormColumns.value.forEach((col) => {
        if (col.FieldGroup && !groups.includes(col.FieldGroup)) {
          groups.push(col.FieldGroup);
        }
      });
      enabledCreateFormColumns.value.forEach((col) => {
        if (col.FieldGroup && !groups.includes(col.FieldGroup)) {
          groups.push(col.FieldGroup);
        }
      });
    }

    return groups;
  });
  const enabledFilterColumns = computed(() => {
    if (!metaData || !metaData) {
      return [];
    }
    return metaData.Columns.filter((col) => {
      return col.ShowInFilter;
    }).sort((a, b) => {
      if (a.FilterOrder > b.FilterOrder) {
        return 1;
      }
      if (a.FilterOrder < b.FilterOrder) {
        return -1;
      }
      return 0;
    });
  });
  const enabledCreateFormColumns = computed(() => {
    if (!metaData || !metaData) {
      return [];
    }
    return metaData.Columns.filter((col) => {
      return col.ShowInCreateForm;
    }).sort((a, b) => {
      if (a.CreateFormOrder > b.CreateFormOrder) {
        return 1;
      }
      if (a.CreateFormOrder < b.CreateFormOrder) {
        return -1;
      }
      return 0;
    });
  });
  const enabledDrilldowns = computed(() => {
    if (!metaData || !metaData) {
      return [];
    }
    return metaData.Drilldowns.filter((col) => {
      return col.EditableInForeignKeyParent;
    }).sort((a, b) => {
      if (a.Sequence > b.Sequence) {
        return 1;
      }
      if (a.Sequence < b.Sequence) {
        return -1;
      }
      return 0;
    });
  });
  const getMetaColumn = (columnName: string) => {
    const matchingColumns = metaData?.Columns.filter((col) => {
      return col.ColumnNamePc == columnName;
    });

    return matchingColumns ? matchingColumns[0] : null;
  };

  const primaryKeyColumnNamePc =
    metaData.PKeys && metaData.PKeys.length > 0
      ? metaData.PKeys[0].ColumnNamePc
      : metaData.Columns.find((col) => col.IsIdentity)?.ColumnNamePc ?? "Id";

  const meta: IMeta = {
    data: metaData,
    enabledGridColumns,
    enabledEditFormColumns,
    fieldGroups,
    enabledFilterColumns,
    enabledCreateFormColumns,
    enabledDrilldowns,
    getMetaColumn,
    columns: metaData ? metaData.Columns : [],
    primaryKeyColumnNamePc,
  } as IMeta;

  return meta;
}

export function ControlTypeToGenericInputType(ControlType: string) {
  const key = ControlType as keyof typeof IMetaControlTypesMapping;
  if (key in IMetaControlTypesMapping) {
    return IMetaControlTypesMapping[key];
  }

  return IMetaControlTypesMapping.default;
}

export function MetaToGenericForm(
  meta: IMeta,
  data: Indexable = {},
  columnFilterCallBack: (col: IMetaColumn) => boolean = (col) =>
    col.ShowInFilter,
) {
  const enabledFilterFormColumns = meta.data.Columns.filter((col) => {
    return columnFilterCallBack(col);
  });

  const genericFormFields: IGenericForm = {};

  enabledFilterFormColumns.forEach((col) => {
    const field: IGenericField = {};

    field.label = col.ColumnLabel;
    // @ts-ignore
    field.inputType = ControlTypeToGenericInputType(col.ControlType);
    field.value = null;
    field.placeholder = col.ColumnLabel;
    field.colspan = {
      sm: col.ColSpanSm,
      md: col.ColSpanMd,
      lg: col.ColSpanLg,
      xl: col.ColSpanXl,
      xxl: col.ColSpanXxl,
    };

    switch (field.inputType) {
      case InputType.SelectDropdown:
      case InputType.RadioSelect:
        field.misc = {
          displayKey: col.DisplayColumnName,
          valueKey: col.ValueColumnName,
          endpoint: col.EndpointRoute,
          fetchOptions: true,
        };
      case InputType.EnumSelect:
        field.misc = {
          displayKey: col.DisplayColumnName,
          valueKey: col.ValueColumnName,
          endpoint: col.EndpointRoute,
          fetchOptions: true,
        };
        break;
      case InputType.EnumSelect:
        field.misc = {
          //       bindings.value.url = props.misc?.url;
          // bindings.value.lookupUrl = props.misc?.lookupUrl;
          url: `/scad/${pluralize(meta.data.Table.TableName)}/${data[meta.primaryKeyColumnNamePc]}`,
          lookupUrl:
            col.ColumnNamePc in data
              ? `/scad/files?TableName=${
                  meta.data.Table.TableName
                }&FilePath=${data[col.ColumnNamePc]}`
              : undefined,
        };
      default:
        break;
    }

    genericFormFields[col.ColumnNamePc] = field;
  });

  return genericFormFields;
}
